import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Customer, CustomerStatus, CustomerType } from "@/mocks/admin/customers";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (customer: Customer) => void;
};

const typeOptions: { key: CustomerType; label: string; icon: string }[] = [
  { key: "individual", label: "Individual", icon: "ri-user-line" },
  { key: "business", label: "Business", icon: "ri-briefcase-line" },
  { key: "property-mgmt", label: "Property Mgmt", icon: "ri-building-line" },
];

const statusOptions: { key: CustomerStatus; label: string; color: string }[] = [
  { key: "lead", label: "Lead", color: "bg-amber-500" },
  { key: "active", label: "Active", color: "bg-emerald-500" },
  { key: "vip", label: "VIP", color: "bg-brand-500" },
  { key: "dormant", label: "Dormant", color: "bg-ink-400" },
];

const sourceOptions = [
  "Google",
  "Yelp",
  "Referral",
  "Instagram",
  "Facebook",
  "Website",
  "Angi",
  "Phone Call",
  "Repeat Customer",
  "Direct",
];

const tagSuggestions = [
  "First-time",
  "Returning",
  "Family",
  "Piano",
  "Fragile",
  "Long-distance",
  "Commercial",
  "Office",
  "Net-30",
  "Studio",
  "Hot lead",
];

const avatarColors = [
  "bg-brand-500",
  "bg-indigo-500",
  "bg-pink-500",
  "bg-emerald-600",
  "bg-fuchsia-500",
  "bg-amber-600",
  "bg-rose-500",
  "bg-teal-600",
  "bg-cyan-600",
  "bg-orange-600",
  "bg-violet-500",
  "bg-lime-600",
  "bg-sky-600",
];

function pickInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function AddCustomerModal({ open, onClose, onCreate }: Props) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [type, setType] = useState<CustomerType>("individual");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("TX");
  const [status, setStatus] = useState<CustomerStatus>("lead");
  const [source, setSource] = useState(sourceOptions[0]);
  const [tags, setTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState("");

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setError("");
      setSubmitting(false);
      setType("individual");
      setName("");
      setEmail("");
      setPhone("");
      setCity("");
      setState("TX");
      setStatus("lead");
      setSource(sourceOptions[0]);
      setTags([]);
      setCustomTag("");
    }
  }, [open]);

  if (!open) return null;

  const toggleTag = (t: string) => {
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  const addCustomTag = () => {
    const t = customTag.trim();
    if (t && !tags.includes(t)) setTags((cur) => [...cur, t]);
    setCustomTag("");
  };

  const canNext1 = type && name.trim();
  const canNext2 = phone.trim() && city.trim() && state.trim();

  const handleSubmit = async () => {
    setError("");
    setSubmitting(true);

    const newId = "CU-" + (1100 + Math.floor(Math.random() * 8900));
    const initials = pickInitials(name);
    const color = avatarColors[Math.floor(Math.random() * avatarColors.length)];

    const { error: insertError } = await supabase.from("customers").insert({
      id: newId,
      name: name.trim(),
      initials,
      color,
      type,
      status,
      email: email.trim() || null,
      phone: phone.trim(),
      city: city.trim(),
      state: state.trim().toUpperCase(),
      source,
      total_moves: 0,
      lifetime_value: 0,
      last_move_date: "—",
      last_move_relative: status === "lead" ? "New inquiry" : "—",
      rating: null,
      tags,
      next_booking_date: null,
    });

    if (insertError) {
      setError(insertError.message);
      setSubmitting(false);
      return;
    }

    const newCustomer: Customer = {
      id: newId,
      name: name.trim(),
      initials,
      color,
      type,
      status,
      email: email.trim(),
      phone: phone.trim(),
      city: city.trim(),
      state: state.trim().toUpperCase(),
      source,
      totalMoves: 0,
      lifetimeValue: 0,
      lastMoveDate: "—",
      lastMoveRelative: status === "lead" ? "New inquiry" : "—",
      rating: null,
      tags,
      nextBookingDate: null,
    };

    onCreate(newCustomer);
    setSubmitting(false);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white w-full max-w-2xl rounded-lg border border-ink-100 max-h-[90vh] flex flex-col pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-brand-100 text-brand-700">
                <i className="ri-user-add-line text-lg"></i>
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-ink-900">Add Customer</h2>
                <div className="text-xs text-ink-500">
                  Step {step} of 3 ·{" "}
                  {step === 1
                    ? "Customer type & name"
                    : step === 2
                    ? "Contact details"
                    : "Status & tags"}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          <div className="px-6 pt-4 pb-2 shrink-0">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s <= step ? "bg-brand-500" : "bg-cream-100"
                  }`}
                ></div>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">
            {step === 1 && (
              <div className="space-y-4">
                <Field label="Customer type" required>
                  <div className="grid grid-cols-3 gap-2">
                    {typeOptions.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setType(t.key)}
                        className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-md border cursor-pointer whitespace-nowrap transition-colors ${
                          type === t.key
                            ? "bg-brand-50 border-brand-500 text-brand-700"
                            : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                        }`}
                      >
                        <i className={`${t.icon} text-lg`}></i>
                        <span className="text-xs font-semibold">{t.label}</span>
                      </button>
                    ))}
                  </div>
                </Field>

                <Field
                  label={type === "individual" ? "Full name" : "Company name"}
                  required
                >
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={
                      type === "individual"
                        ? "e.g. Margaret Sullivan"
                        : type === "business"
                        ? "e.g. Genesis Holdings LLC"
                        : "e.g. Cypress Property Group"
                    }
                    className="form-input"
                  />
                  {name && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-8 h-8 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center">
                        {pickInitials(name)}
                      </div>
                      <span className="text-xs text-ink-500">Avatar preview</span>
                    </div>
                  )}
                </Field>

                <Field label="How they found us">
                  <select
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    className="form-input pr-8"
                  >
                    {sourceOptions.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Phone" required>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(512) 555-0142"
                      className="form-input"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="customer@email.com"
                      className="form-input"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Field label="City" required>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-ink-400">
                          <i className="ri-map-pin-line text-sm"></i>
                        </span>
                        <input
                          type="text"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="e.g. Austin"
                          className="form-input pl-9"
                        />
                      </div>
                    </Field>
                  </div>
                  <Field label="State" required>
                    <input
                      type="text"
                      maxLength={2}
                      value={state}
                      onChange={(e) => setState(e.target.value.toUpperCase())}
                      placeholder="TX"
                      className="form-input uppercase"
                    />
                  </Field>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <Field label="Customer status">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {statusOptions.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setStatus(s.key)}
                        className={`text-sm font-semibold px-3 py-2.5 rounded-md border cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2 ${
                          status === s.key
                            ? "bg-ink-900 text-white border-ink-900"
                            : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${s.color}`}></span>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Tags">
                  <div className="text-[11px] text-ink-500 mb-2">
                    Tap to toggle, or add a custom one.
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {tagSuggestions.map((t) => {
                      const active = tags.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => toggleTag(t)}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
                            active
                              ? "bg-brand-500 text-white border-brand-500"
                              : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                          }`}
                        >
                          {active && <i className="ri-check-line mr-1"></i>}
                          {t}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={customTag}
                      onChange={(e) => setCustomTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addCustomTag();
                        }
                      }}
                      placeholder="Custom tag…"
                      className="form-input flex-1"
                    />
                    <button
                      type="button"
                      onClick={addCustomTag}
                      disabled={!customTag.trim()}
                      className="inline-flex items-center gap-1 px-3 py-2.5 rounded-md text-xs font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap disabled:opacity-40"
                    >
                      <i className="ri-add-line"></i>
                      Add
                    </button>
                  </div>
                  {tags.filter((t) => !tagSuggestions.includes(t)).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {tags
                        .filter((t) => !tagSuggestions.includes(t))
                        .map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold bg-cream-100 text-ink-700 px-2 py-1 rounded-full"
                          >
                            {t}
                            <button
                              onClick={() => toggleTag(t)}
                              className="text-ink-400 hover:text-ink-700 cursor-pointer"
                            >
                              <i className="ri-close-line"></i>
                            </button>
                          </span>
                        ))}
                    </div>
                  )}
                </Field>

                {error && (
                  <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded-md">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{error}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-ink-100 bg-cream-50 flex items-center justify-between gap-2 shrink-0 rounded-b-lg">
            <button
              onClick={onClose}
              className="text-sm font-semibold text-ink-500 hover:text-ink-900 px-3 py-2 cursor-pointer"
            >
              Cancel
            </button>
            <div className="flex items-center gap-2">
              {step > 1 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold border border-ink-200 text-ink-700 hover:bg-white cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={step === 1 ? !canNext1 : !canNext2}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue
                  <i className="ri-arrow-right-line"></i>
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Saving…
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line"></i>
                      Add Customer
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}